import {
  useErrorHandlingMutation,
  useErrorHandlingQuery,
} from '@backoffice-frontend/data-fetching'
import {
  Avatar,
  Box,
  NotificationBadge,
  UnstyledButton,
} from '@backoffice-frontend/patterns'
import { useBackofficeStatusesQuery } from '@backoffice-frontend/status-api'
import { useBackofficeUpdatesQuery } from '@backoffice-frontend/updates-api'
import { useBackofficeLoggedInUser } from '@backoffice-frontend/user-management-ui'
import {
  ActionItem,
  ActionMenu,
  useActionMenu,
  useSelectionMenu,
} from '@moia-dev/pace-web'
import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useBackofficeStatusGetLastVisitForBadgeQuery,
  useBackofficeStatusSetLastVisitMutation,
  useBackofficeUpdateGetLastVisitForBadgeQuery,
  useBackofficeUpdateSetLastVisitMutation,
} from './AccountBadge.hook'
import { AccountBadgeGroupSwitcher } from './AccountBadgeMenu/AccountBadgeGroupSwitcher/AccountBadgeGroupSwitcher'
import { AccountBadgeLanguageSwitch } from './AccountBadgeMenu/AccountBadgeLanguageSwitch'
import { AccountBadgeMainMenu } from './AccountBadgeMenu/AccountBadgeMainMenu'
import { AccountBadgeThemeSwitch } from './AccountBadgeMenu/AccountBadgeThemeSwitch'
import { MenuState, type MenuStateType } from './AccountBadgeMenu/MenuState'

const MENU_WIDTH = 210

const getUserInitials = (userName: string): string => {
  const userNameSplit = userName ? userName.split('.') : ''
  const lastNameInitial =
    userNameSplit[1]?.charAt(0) || userNameSplit[0]?.charAt(1)
  return `${userNameSplit[0]?.charAt(0)}${lastNameInitial}`.toUpperCase()
}

export const AccountBadge = () => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [isGroupSwitcherOpen, setIsGroupSwitcherOpen] = useState(false)
  const [currentMenu, setCurrentMenu] = useState<MenuStateType>(MenuState.main)

  const { user: loggedInUser } = useBackofficeLoggedInUser()

  const userNameInitials = loggedInUser
    ? getUserInitials(loggedInUser.username)
    : ''

  const { data: updatesData } = useBackofficeUpdatesQuery()

  const { data: statusesData } = useErrorHandlingQuery(
    useBackofficeStatusesQuery,
    {
      variables: {},
    },
  )
  const { data: statusLastVisit, refetch: refetchStatusLastVisit } =
    useErrorHandlingQuery(useBackofficeStatusGetLastVisitForBadgeQuery, {
      variables: {},
    })
  const { data: updateLastVisit, refetch: refetchUpdateLastVisit } =
    useErrorHandlingQuery(useBackofficeUpdateGetLastVisitForBadgeQuery, {
      variables: {},
    })

  const [setUpdatesLastVisited] = useErrorHandlingMutation(
    useBackofficeUpdateSetLastVisitMutation,
  )
  const [setStatusLastVisited] = useErrorHandlingMutation(
    useBackofficeStatusSetLastVisitMutation,
  )

  const hasNotificationsUpdates =
    !!updatesData?.backofficeUpdates.updates.some(
      update =>
        update?.creationDate &&
        updateLastVisit?.lastVisit.lastVisitDate &&
        update.creationDate > updateLastVisit?.lastVisit.lastVisitDate,
    ) || false

  const hasNotificationsStatuses =
    !!statusesData?.backofficeStatuses.statuses.some(
      status =>
        status?.creationDate &&
        statusLastVisit?.lastVisit.lastVisitDate &&
        status.creationDate > statusLastVisit?.lastVisit.lastVisitDate,
    ) || false

  const hasNotifications = hasNotificationsUpdates || hasNotificationsStatuses

  const { getReferenceProps, ...props } = useActionMenu({
    isOpen,
    setIsOpen,
  })
  const groupSwitcherSelectionMenu = useSelectionMenu({
    isOpen: isGroupSwitcherOpen,
    setIsOpen: setIsGroupSwitcherOpen,
  })

  useEffect(() => {
    if (!isOpen) {
      setCurrentMenu(MenuState.main)
    }
    if (isOpen) {
      setIsGroupSwitcherOpen(false)
    }
  }, [isOpen])

  return (
    <>
      <Box {...getReferenceProps()} ref={props.refs.setReference}>
        <UnstyledButton ref={groupSwitcherSelectionMenu.refs.setReference}>
          <NotificationBadge active={hasNotifications}>
            <Avatar small active={isOpen}>
              {userNameInitials}
            </Avatar>
          </NotificationBadge>
        </UnstyledButton>
        <ActionMenu {...props}>
          <motion.div
            animate={{
              transition: { duration: 0.2, ease: 'easeInOut' },
            }}
            style={{ overflow: 'hidden', width: MENU_WIDTH }}
          >
            <AnimatePresence mode="wait" initial={false}>
              {currentMenu === MenuState.main && (
                <AccountBadgeMainMenu
                  setCurrentMenu={setCurrentMenu}
                  setIsOpen={setIsOpen}
                  hasNotificationsUpdates={hasNotificationsUpdates}
                  hasNotificationsStatuses={hasNotificationsStatuses}
                  setUpdatesLastVisited={setUpdatesLastVisited}
                  refetchUpdateLastVisit={refetchUpdateLastVisit}
                  setStatusLastVisited={setStatusLastVisited}
                  refetchStatusLastVisit={refetchStatusLastVisit}
                  groupSwitcherActionItem={
                    <ActionItem
                      {...groupSwitcherSelectionMenu.getReferenceProps({
                        onClick: () => setIsOpen(false),
                      })}
                      label={t('authenticatedAppView_groupSwitcher_button')}
                    />
                  }
                />
              )}
              {currentMenu === MenuState.language && (
                <AccountBadgeLanguageSwitch setCurrentMenu={setCurrentMenu} />
              )}
              {currentMenu === MenuState.theme && (
                <AccountBadgeThemeSwitch setCurrentMenu={setCurrentMenu} />
              )}
            </AnimatePresence>
          </motion.div>
        </ActionMenu>
      </Box>
      {loggedInUser && isGroupSwitcherOpen && (
        <AccountBadgeGroupSwitcher
          selectionMenuProps={groupSwitcherSelectionMenu}
          user={loggedInUser}
        />
      )}
    </>
  )
}
